import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PlatformSettingsService } from '~/app/services/settings/platform-settings.service';
import {
  flattenHistoryPages,
  getNextHistoryPageParam,
  HISTORY_PAGE_SIZE,
  shouldLoadMoreHistory,
} from '../utils';

export function useSettingHistory(settingKey: string | null) {
  const historyQuery = useInfiniteQuery({
    queryKey: ['platform-settings', 'history', settingKey],
    queryFn: ({ pageParam }) =>
      PlatformSettingsService.history(settingKey ?? '', {
        page: pageParam,
        limit: HISTORY_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: getNextHistoryPageParam,
    enabled: Boolean(settingKey),
  });

  const { items, total } = useMemo(
    () => flattenHistoryPages(historyQuery.data?.pages),
    [historyQuery.data?.pages],
  );

  function onScroll(event: { currentTarget: HTMLElement }): void {
    const target = event.currentTarget;
    if (
      !shouldLoadMoreHistory({
        scrollTop: target.scrollTop,
        clientHeight: target.clientHeight,
        scrollHeight: target.scrollHeight,
        hasNextPage: Boolean(historyQuery.hasNextPage),
        isFetchingNextPage: historyQuery.isFetchingNextPage,
      })
    ) {
      return;
    }
    void historyQuery.fetchNextPage();
  }

  return {
    items,
    total,
    isLoading: historyQuery.isLoading,
    isFetchingNextPage: historyQuery.isFetchingNextPage,
    hasError: historyQuery.isError,
    onScroll,
  };
}
